import React, { useState, useEffect } from 'react';
import axios from 'axios';
import HeaderBar from '../components/HeaderBar';
import './AdminDashboard.css';

function AdminDashboard({ user, onLogout }) {
    const [analytics, setAnalytics] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastApiError, setLastApiError] = useState(null);
    const debugMode = new URLSearchParams(window.location.search).get('debug') === '1' || process.env.NODE_ENV !== 'production';
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterArrival, setFilterArrival] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [allDesks, setAllDesks] = useState([]);
    const [editingBooking, setEditingBooking] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Maintenance Mode State
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [notificationMode, setNotificationMode] = useState('default');
    const [resendingEmails, setResendingEmails] = useState(false);

    useEffect(() => {
        fetchMaintenanceStatus();
        fetchNotificationMode();
    }, []); // Add selectedMonth to dependency array if fetchStats uses it

    const fetchMaintenanceStatus = async () => {
        try {
            const res = await axios.get('/api/maintenance/status');
            setMaintenanceMode(res.data.maintenance_mode);
        } catch (error) {
            console.error('Error fetching maintenance status:', error);
        }
    };

    const fetchNotificationMode = async () => {
        try {
            const res = await axios.get('/api/admin/notification-mode');
            setNotificationMode(res.data.mode || 'default');
        } catch (error) {
            console.error('Error fetching notification mode:', error);
        }
    };

    const toggleMaintenanceMode = async () => {
        const newStatus = !maintenanceMode;
        if (window.confirm(`Are you sure you want to turn ${newStatus ? 'ON' : 'OFF'} maintenance mode?`)) {
            try {
                // Determine header based on token presence
                const token = localStorage.getItem('token');
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                await axios.post('/api/maintenance/status', { maintenance_mode: newStatus }, { headers });
                setMaintenanceMode(newStatus);
            } catch (error) {
                console.error('Error updating maintenance status:', error);
                alert('Failed to update status');
            }
        }
    };

    const updateNotificationMode = async (newMode) => {
        if (window.confirm(`Are you sure you want to switch global notification mode to: ${newMode.toUpperCase()}?`)) {
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                await axios.post('/api/admin/notification-mode', { mode: newMode }, { headers });
                setNotificationMode(newMode);
            } catch (error) {
                console.error('Error updating notification mode:', error);
                alert('Failed to update notification mode');
            }
        }
    };

    const handleEmergencyResend = async () => {
        if (window.confirm('🚨 EMERGENCY RESEND: This will send arrival confirmation emails to ALL users with today\'s bookings whose original preference was WhatsApp but haven\'t confirmed yet. Are you sure?')) {
            try {
                setResendingEmails(true);
                const token = localStorage.getItem('token');
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                const res = await axios.post('/api/admin/emergency-resend-checkin', {}, { headers });
                alert(res.data.message || `Successfully resent ${res.data.count} emails.`);
            } catch (error) {
                console.error('Error during emergency resend:', error);
                alert('Emergency resend failed: ' + (error.response?.data?.error || error.message));
            } finally {
                setResendingEmails(false);
            }
        }
    };



    const [reportType, setReportType] = useState('monthly');
    const [reportPeriod, setReportPeriod] = useState('current');
    const [sharePointStatus, setSharePointStatus] = useState(null);
    const [uploadingToSharePoint, setUploadingToSharePoint] = useState(false);
    const [reportModal, setReportModal] = useState(null);
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [triggeringReport, setTriggeringReport] = useState(false);
    const [emailPopup, setEmailPopup] = useState({ show: false, reportType: '', emails: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [currentDeskPage, setCurrentDeskPage] = useState(1);
    const [itemsPerPage] = useState(20);

    // Meeting room bookings state
    const [bookingType, setBookingType] = useState('desk'); // 'desk' or 'meeting_room'
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [loadingMeetingRooms, setLoadingMeetingRooms] = useState(false);

    useEffect(() => {
        console.log('useEffect triggered for selectedMonth:', selectedMonth);
        if (user?.is_admin) {
            fetchData();
            loadAllBookings();
        }
    }, [user, selectedMonth]);

    const fetchData = async () => {
        console.log('fetchData called for month:', selectedMonth);
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            console.log('Fetching analytics with token:', !!token);
            const [analyticsResponse, desksResponse] = await Promise.all([
                fetch(`/api/admin/analytics?month=${selectedMonth}`, { headers }),
                fetch('/api/desks', { headers })
            ]);

            console.log('Analytics response status:', analyticsResponse.status);
            console.log('Desks response status:', desksResponse.status);

            if (!analyticsResponse.ok) {
                console.error('Analytics fetch failed:', await analyticsResponse.text());
                setLoading(false);
                return;
            }

            const analyticsData = await analyticsResponse.json();
            const desksData = await desksResponse.json();

            console.log('Analytics data received:', analyticsData);

            // Analytics API returns data directly (no success wrapper)
            if (analyticsData && analyticsData.totalBookings !== undefined) {
                console.log('Setting analytics data');
                setAnalytics(analyticsData);
                setLoading(false);
            } else {
                console.error('Invalid analytics data:', analyticsData);
                setLoading(false);
            }

            // Desks API returns array directly (no wrapper object)
            if (Array.isArray(desksData) && desksData.length > 0) {
                // Get current bookings for each desk
                const desksWithBookings = await Promise.all(
                    desksData.map(async (desk) => {
                        try {
                            const today = new Date().toISOString().split('T')[0];
                            const futureDate = new Date();
                            futureDate.setDate(futureDate.getDate() + 30);
                            const endDate = futureDate.toISOString().split('T')[0];

                            const bookingResponse = await fetch(`/api/admin/desk-booking-impact/${desk.id}?startDate=${today}&endDate=${endDate}`, { headers });
                            const bookingData = await bookingResponse.json();
                            return {
                                ...desk,
                                hasActiveBookings: bookingData.impactedBookings && bookingData.impactedBookings.length > 0,
                                activeBookingCount: bookingData.impactedBookings ? bookingData.impactedBookings.length : 0
                            };
                        } catch (error) {
                            return {
                                ...desk,
                                hasActiveBookings: false,
                                activeBookingCount: 0
                            };
                        }
                    })
                );
                setAllDesks(desksWithBookings);
            } else {
                // Set empty array if no desks found
                setAllDesks([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setLastApiError(error?.message || String(error));
            setLoading(false);
        }
    }; const loadAllBookings = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const response = await axios.get('/api/admin/bookings', { headers });
            // Filter out bookings with null/invalid IDs
            const validBookings = response.data.filter(booking => booking.id && booking.id !== null);
            setBookings(validBookings);
        } catch (err) {
            console.error('Failed to load bookings', err);
            setLastApiError(err?.message || String(err));
        }
    };

    const handleCancelBooking = async (bookingId) => {
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;

        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            await axios.delete(`/api/admin/bookings/${bookingId}`, { headers });
            loadAllBookings();
            fetchData();
        } catch (err) {
            alert('Failed to cancel booking');
        }
    };

    const handleEditBooking = async (updatedBooking) => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            await axios.put(`/api/admin/bookings/${editingBooking.id}`, updatedBooking, { headers });
            setEditingBooking(null);
            setEditForm({});
            loadAllBookings();
            fetchData();
        } catch (err) {
            alert('Failed to update booking');
        }
    };

    const generateReport = async (type) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const response = await fetch(`/api/admin/reports/${type}?month=${selectedMonth}`, { headers });

            if (type === 'pdf') {
                const htmlContent = await response.text();

                // Check if response is actually HTML or just "Loading application"
                if (htmlContent.includes('Loading application') || htmlContent.trim().length < 100) {
                    alert('Report generation failed. Please try again in a few moments.');
                    return;
                }

                const blob = new Blob([htmlContent], { type: 'text/html' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `UNICEF-Desk-Report-${selectedMonth}.html`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                // Inform user that HTML report is downloaded for viewing/printing to PDF
                alert(`Report downloaded as HTML file. You can open it and use your browser's "Print to PDF" function to save as PDF.`);
            } else if (type === 'csv') {
                const csvContent = await response.text();
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `Desk-Bookings-${selectedMonth}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else if (type === 'summary') {
                const data = await response.json();
                // Create a modal or new window with executive summary
                showExecutiveSummary(data);
            }
        } catch (error) {
            console.error('Error generating report:', error);
            setLastApiError(error?.message || String(error));
            alert('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Generate Executive Summary PDF with AI analytics
    const generateExecutiveSummaryPDF = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/reports/executive-summary/pdf?month=${selectedMonth}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to generate PDF report');
            }

            // Download the PDF
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `Executive_Summary_${selectedMonth}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            alert('Executive Summary PDF generated successfully!');
        } catch (error) {
            console.error('Error generating Executive Summary PDF:', error);
            setLastApiError(error?.message || String(error));
            alert('Failed to generate Executive Summary PDF. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Custom date range state
    const [useCustomDates, setUseCustomDates] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Enhanced report download function using new Reports API
    const downloadReport = async (reportName) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            let url = '';
            let filename = '';

            const isCustom = useCustomDates && customStartDate && customEndDate;

            switch (reportName) {
                case 'daily':
                    const today = new Date().toISOString().split('T')[0];
                    url = `/api/reports/daily/csv?date=${today}`;
                    filename = `occupancy_daily_${today}.csv`;
                    break;
                case 'weekly':
                    const now = new Date();
                    const weekNum = getWeekNumber(now);
                    url = `/api/reports/weekly/csv?week=${now.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
                    filename = `occupancy_weekly_${now.getFullYear()}-W${String(weekNum).padStart(2, '0')}.csv`;
                    break;
                case 'monthly':
                    // Monthly report is strictly month-based
                    url = `/api/reports/monthly/csv?month=${selectedMonth}`;
                    filename = `occupancy_monthly_${selectedMonth}.csv`;
                    break;
                case 'bookings':
                    if (isCustom) {
                        url = `/api/reports/bookings/csv?startDate=${customStartDate}&endDate=${customEndDate}`;
                        filename = `bookings_${customStartDate}_to_${customEndDate}.csv`;
                    } else {
                        url = `/api/reports/bookings/csv?month=${selectedMonth}`;
                        filename = `bookings_${selectedMonth}.csv`;
                    }
                    break;
                case 'attendance':
                    if (isCustom) {
                        url = `/api/reports/attendance/csv?startDate=${customStartDate}&endDate=${customEndDate}`;
                        filename = `attendance_${customStartDate}_to_${customEndDate}.csv`;
                    } else {
                        url = `/api/reports/attendance/csv?month=${selectedMonth}`;
                        filename = `attendance_${selectedMonth}.csv`;
                    }
                    break;
                case 'unit-summary':
                    if (isCustom) {
                        url = `/api/reports/unit-summary/csv?startDate=${customStartDate}&endDate=${customEndDate}`;
                        filename = `unit_summary_${customStartDate}_to_${customEndDate}.csv`;
                    } else {
                        url = `/api/reports/unit-summary/csv?month=${selectedMonth}`;
                        filename = `unit_summary_${selectedMonth}.csv`;
                    }
                    break;
                default:
                    throw new Error('Unknown report type');
            }

            const response = await fetch(url, { headers });
            if (!response.ok) {
                throw new Error('Failed to download report');
            }

            const csvContent = await response.text();
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading report:', error);
            alert('Failed to download report: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get ISO week number
    const getWeekNumber = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        return weekNo;
    };

    // View detailed report (JSON)
    const viewDetailedReport = async (reportName) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            let url = '';
            switch (reportName) {
                case 'daily':
                    const today = new Date().toISOString().split('T')[0];
                    url = `/api/reports/daily?date=${today}`;
                    break;
                case 'weekly':
                    const now = new Date();
                    const weekNum = getWeekNumber(now);
                    url = `/api/reports/weekly?week=${now.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
                    break;
                case 'monthly':
                    url = `/api/reports/monthly?month=${selectedMonth}`;
                    break;
                default:
                    throw new Error('Unknown report type');
            }

            const response = await fetch(url, { headers });
            if (!response.ok) {
                throw new Error('Failed to fetch report');
            }

            const data = await response.json();
            showDetailedReport(data);
        } catch (error) {
            console.error('Error viewing report:', error);
            alert('Failed to view report: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Show detailed report in professional modal
    const showDetailedReport = (data) => {
        let title = '';
        let subtitle = '';
        let sections = [];
        let rawText = '';

        if (data.report_type === 'daily') {
            title = '📊 Daily Occupancy Report';
            subtitle = new Date(data.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            sections = [
                {
                    icon: '🏢',
                    title: 'Overview',
                    items: [
                        { label: 'Total Desks', value: data.total_desks },
                        { label: 'Desks Used', value: data.desks_used },
                        { label: 'Occupancy Rate', value: `${data.occupancy_rate}%` }
                    ]
                },
                {
                    icon: '👥',
                    title: 'Attendance',
                    items: [
                        { label: 'Staff Present', value: data.staff_attendance },
                        { label: 'No-Shows', value: data.no_show_count || 0 },
                        { label: 'Total Bookings', value: data.total_bookings }
                    ]
                }
            ];
            rawText = `DAILY OCCUPANCY REPORT\nDate: ${data.date}\nTotal Desks: ${data.total_desks}\nDesks Used: ${data.desks_used}\nOccupancy Rate: ${data.occupancy_rate}%\nStaff Attendance: ${data.staff_attendance}\nNo-Shows: ${data.no_show_count || 0}`;
        } else if (data.report_type === 'weekly') {
            title = '📊 Weekly Occupancy Report';
            subtitle = `${data.week_label} (${data.start_date} to ${data.end_date})`;
            sections = [
                {
                    icon: '📈',
                    title: 'Weekly Statistics',
                    items: [
                        { label: 'Total Bookings', value: data.total_bookings },
                        { label: 'Occupancy Rate', value: `${data.occupancy_rate}%` },
                        { label: 'Total Desks', value: data.total_desks },
                        { label: 'Staff Attendance', value: data.staff_attendance }
                    ]
                }
            ];
            if (data.trend) {
                sections.push({
                    icon: '📊',
                    title: 'Week-over-Week Trend',
                    items: [
                        { label: 'Change', value: `${data.trend.direction === 'up' ? '↑' : '↓'} ${Math.abs(data.trend.change_percent)}%` },
                        { label: 'Previous Week', value: `${data.trend.previous_week_bookings} bookings` }
                    ]
                });
            }
            rawText = `WEEKLY OCCUPANCY REPORT\nWeek: ${data.week_label}\nPeriod: ${data.start_date} to ${data.end_date}\nTotal Bookings: ${data.total_bookings}\nOccupancy Rate: ${data.occupancy_rate}%`;
        } else if (data.report_type === 'monthly') {
            title = '📊 Monthly Occupancy Report';
            subtitle = new Date(data.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            sections = [
                {
                    icon: '📈',
                    title: 'Monthly Statistics',
                    items: [
                        { label: 'Total Bookings', value: data.total_bookings },
                        { label: 'Occupancy Rate', value: `${data.occupancy_rate}%` },
                        { label: 'Total Desks', value: data.total_desks },
                        { label: 'Days in Month', value: data.days_in_month }
                    ]
                },
                {
                    icon: '👥',
                    title: 'Attendance',
                    items: [
                        { label: 'Unique Users', value: data.staff_attendance },
                        { label: 'No-Show Rate', value: `${data.no_show_rate || 0}%` }
                    ]
                }
            ];
            if (data.most_reserved_desks && data.most_reserved_desks.length > 0) {
                sections.push({
                    icon: '🏆',
                    title: 'Top Desks',
                    items: data.most_reserved_desks.slice(0, 5).map((desk, i) => ({
                        label: `#${i + 1} - Desk ${desk.desk_id}`,
                        value: `${desk.count} bookings`
                    }))
                });
            }
            if (data.most_frequent_users && data.most_frequent_users.length > 0) {
                sections.push({
                    icon: '👤',
                    title: 'Top Users',
                    items: data.most_frequent_users.slice(0, 5).map((user, i) => ({
                        label: `#${i + 1} - ${user.user_name || user.user_email?.split('@')[0] || 'Unknown'}`,
                        value: `${user.count} bookings`
                    }))
                });
            }
            rawText = `MONTHLY OCCUPANCY REPORT\nMonth: ${data.month}\nTotal Bookings: ${data.total_bookings}\nOccupancy Rate: ${data.occupancy_rate}%\nStaff Attendance: ${data.staff_attendance} unique users`;
        }

        setReportModal({ title, subtitle, sections, rawText });
    };

    // Close report modal
    const closeReportModal = () => {
        setReportModal(null);
    };

    const showExecutiveSummary = (data) => {
        const summaryText = `
📊 DESK BOOKING EXECUTIVE SUMMARY
Month: ${new Date(selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
Generated: ${new Date().toLocaleDateString()}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📈 KEY METRICS:
• Total Bookings: ${data.totalBookings}
• Utilization Rate: ${data.utilizationRate}%
• Most Popular Desk: ${data.mostReservedDesk?.desk_id || 'N/A'} (${data.mostReservedDesk?.count || 0} bookings)
• Most Active User: ${data.mostFrequentUser?.user_name || 'N/A'} (${data.mostFrequentUser?.count || 0} bookings)

💡 INSIGHTS:
${data.utilizationRate > 80 ?
                '⚠️ High utilization detected. Consider adding more desks or implementing booking limits.' :
                data.utilizationRate > 50 ?
                    '✅ Good utilization rate. Monitor trends for capacity planning.' :
                    '📉 Low utilization. Consider desk optimization or promoting the booking system.'
            }

🎯 RECOMMENDATIONS:
${data.recommendations || 'Continue monitoring usage patterns for optimal desk allocation.'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
© ${new Date().getFullYear()} UNICEF Indonesia - Desk Booking System
        `;

        // Create a modal or alert with the summary
        alert(summaryText);

        // Also copy to clipboard for easy sharing
        if (navigator.clipboard) {
            navigator.clipboard.writeText(summaryText).then(() => {
                console.log('Executive summary copied to clipboard');
            }).catch(err => {
                console.log('Could not copy to clipboard:', err);
            });
        }
    };

    // SharePoint Integration Functions
    const checkSharePointStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            const response = await fetch('/api/reports/sharepoint/status', { headers });
            const data = await response.json();
            setSharePointStatus(data);
        } catch (error) {
            console.error('Failed to check SharePoint status:', error);
            setSharePointStatus({ enabled: false, error: error.message });
        }
    };

    const uploadToSharePoint = async (reportType) => {
        try {
            setUploadingToSharePoint(true);
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await fetch('/api/reports/sharepoint/upload', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    reportType,
                    month: selectedMonth
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✅ Report uploaded to SharePoint!\n\nFile: ${data.filename}\nLocation: ${data.webUrl || 'SharePoint folder'}`);
            } else {
                alert(`❌ Upload failed: ${data.error}`);
            }
        } catch (error) {
            console.error('SharePoint upload error:', error);
            alert(`❌ Upload failed: ${error.message}`);
        } finally {
            setUploadingToSharePoint(false);
        }
    };

    const uploadAllToSharePoint = async () => {
        try {
            setUploadingToSharePoint(true);
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const reportTypes = ['monthly', 'bookings', 'attendance', 'unit-summary'];
            const results = [];

            for (const reportType of reportTypes) {
                try {
                    const response = await fetch('/api/reports/sharepoint/upload', {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            reportType,
                            month: selectedMonth
                        })
                    });
                    const data = await response.json();
                    results.push({ reportType, success: data.success, filename: data.filename });
                } catch (e) {
                    results.push({ reportType, success: false, error: e.message });
                }
            }

            const successCount = results.filter(r => r.success).length;
            const summary = results.map(r =>
                `${r.success ? '✅' : '❌'} ${r.reportType}: ${r.success ? r.filename : r.error}`
            ).join('\n');

            alert(`SharePoint Upload Complete\n\n${successCount}/${reportTypes.length} reports uploaded:\n\n${summary}`);
        } catch (error) {
            console.error('SharePoint bulk upload error:', error);
            alert(`❌ Bulk upload failed: ${error.message}`);
        } finally {
            setUploadingToSharePoint(false);
        }
    };

    // Check Scheduler status
    const checkSchedulerStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            const response = await fetch('/api/reports/scheduler/status', { headers });
            const data = await response.json();
            setSchedulerStatus(data);
        } catch (error) {
            console.error('Failed to check scheduler status:', error);
            setSchedulerStatus({ enabled: false, error: error.message });
        }
    };

    // Open email popup before generating report
    const openEmailPopup = (type) => {
        // Pre-fill with default recipient
        const defaultEmail = schedulerStatus?.recipients?.join(', ') || 'hidsjakarta@unicef.org';
        setEmailPopup({ show: true, reportType: type, emails: defaultEmail });
    };

    // Close email popup
    const closeEmailPopup = () => {
        setEmailPopup({ show: false, reportType: '', emails: '' });
    };

    // Trigger manual report generation with custom recipients
    const triggerScheduledReport = async (type, emails) => {
        try {
            setTriggeringReport(true);
            closeEmailPopup();

            const token = localStorage.getItem('token');
            const response = await fetch('/api/reports/scheduler/trigger', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    type,
                    recipients: emails // Send the email addresses
                })
            });
            const data = await response.json();
            if (data.success) {
                const recipientList = data.recipients?.join(', ') || emails;
                alert(`${type.charAt(0).toUpperCase() + type.slice(1)} report generated and sent successfully!\n\nSent to: ${recipientList}\n\nFile saved to: ${data.filepath || data.csvFilepath}`);
            } else {
                alert(`Failed to generate report: ${data.error}`);
            }
        } catch (error) {
            console.error('Failed to trigger report:', error);
            alert('Failed to trigger report generation');
        } finally {
            setTriggeringReport(false);
        }
    };

    // Check SharePoint and Scheduler status on mount
    useEffect(() => {
        if (user?.is_admin) {
            checkSharePointStatus();
            checkSchedulerStatus();
        }
    }, [user]);

    const filteredBookings = bookings.filter(booking => {
        // Filter by selected month (same as analytics)
        if (booking.booking_date) {
            const bookingMonth = booking.booking_date.substring(0, 7); // YYYY-MM
            if (bookingMonth !== selectedMonth) {
                return false;
            }

            // Additional date range filter within the month
            if (startDate && booking.booking_date < startDate) {
                return false;
            }
            if (endDate && booking.booking_date > endDate) {
                return false;
            }
        }
        if (filterStatus !== 'all' && booking.status !== filterStatus) {
            return false;
        }

        // Arrival filter
        if (filterArrival !== 'all') {
            const hasArrival = booking.checkin_confirmed_at != null;
            if (filterArrival === 'confirmed' && !hasArrival) return false;
            if (filterArrival === 'pending' && hasArrival) return false;
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                booking.user_email?.toLowerCase().includes(term) ||
                booking.user_name?.toLowerCase().includes(term) ||
                booking.desk_id?.toLowerCase().includes(term)
            );
        }
        return true;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBookings = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus, filterArrival, startDate, endDate, selectedMonth]);

    // Desk Pagination Logic
    const totalDeskPages = Math.ceil(allDesks.length / itemsPerPage);
    const indexOfLastDesk = currentDeskPage * itemsPerPage;
    const indexOfFirstDesk = indexOfLastDesk - itemsPerPage;
    const currentDesks = allDesks.slice(indexOfFirstDesk, indexOfLastDesk);

    console.log('AdminDashboard render - loading:', loading, 'analytics:', !!analytics, 'user:', user);

    if (!user?.is_admin) {
        return (
            <div className="admin-dashboard">
                <HeaderBar user={user} onLogout={onLogout} />
                <div className="container">
                    <h2>Access Denied</h2>
                    <p>You need admin privileges to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="dashboard-container">
                <h1>Admin Dashboard</h1>

                {/* Month Selector */}
                <div className="month-selector">
                    <label>Select Month:</label>
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => {
                            console.log('Month changed to:', e.target.value);
                            setSelectedMonth(e.target.value);
                        }}
                    />
                </div>

                {/* Analytics Cards */}
                {loading ? (
                    <div className="loading">Loading analytics...</div>
                ) : analytics ? (
                    <div className="analytics-grid">
                        {/* Maintenance Mode Control */}
                        <div className="stat-card maintenance-card" style={{ borderLeft: maintenanceMode ? '4px solid #f59e0b' : '4px solid #10b981' }}>
                            <div className="stat-icon">{maintenanceMode ? '⚠️' : '✅'}</div>
                            <div className="stat-content">
                                <h3>Maintenance Mode</h3>
                                <div className="stat-value" style={{ fontSize: '1.5rem', marginBottom: '10px' }}>
                                    {maintenanceMode ? 'Active' : 'Inactive'}
                                </div>
                                <button
                                    onClick={toggleMaintenanceMode}
                                    style={{
                                        backgroundColor: maintenanceMode ? '#10b981' : '#f59e0b',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Turn {maintenanceMode ? 'OFF' : 'ON'}
                                </button>
                            </div>
                        </div>



                        <div className="stat-card">
                            <div className="stat-icon">📅</div>
                            <div className="stat-content">
                                <h3>Total Bookings</h3>
                                <div className="stat-value">{analytics.totalBookings || 0}</div>
                                <div className="stat-label">in {new Date(selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🏆</div>
                            <div className="stat-content">
                                <h3>Most Reserved Desk</h3>
                                <div className="stat-value">{analytics.mostReservedDesk?.desk_id || 'N/A'}</div>
                                <div className="stat-label">{analytics.mostReservedDesk?.count || 0} bookings</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">👤</div>
                            <div className="stat-content">
                                <h3>Most Frequent User</h3>
                                <div className="stat-value">{analytics.mostFrequentUser?.user_name || 'N/A'}</div>
                                <div className="stat-label">{analytics.mostFrequentUser?.count || 0} bookings</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">📊</div>
                            <div className="stat-content">
                                <h3>Desk Utilization</h3>
                                <div className="stat-value">{analytics.utilizationRate || 0}%</div>
                                <div className="stat-label">Average occupancy</div>
                            </div>
                        </div>

                        <div className="stat-card occupancy-card">
                            <div className="stat-icon">🏢</div>
                            <div className="stat-content">
                                <h3>Current Occupancy</h3>
                                <div className="stat-value">{analytics.currentOccupancy || 0}%</div>
                                <div className="stat-label">{analytics.occupiedDesks || 0} of {analytics.totalDesks || 0} desks occupied</div>
                            </div>
                        </div>

                        <div className="stat-card trend-card">
                            <div className="stat-icon">📈</div>
                            <div className="stat-content">
                                <h3>Usage Trend</h3>
                                <div className="stat-value">{analytics.trendDirection || ''}{analytics.trendChange || 0}%</div>
                                <div className="stat-label">{analytics.trendDescription || 'vs last month'}</div>
                            </div>
                        </div>

                        <div className="stat-card forecast-card">
                            <div className="stat-icon">🔮</div>
                            <div className="stat-content">
                                <h3>Next Week Forecast</h3>
                                <div className="stat-value">{analytics.forecastBookings || 0}</div>
                                <div className="stat-label">Predicted bookings</div>
                            </div>
                        </div>

                        {/* Notification Mode Toggle (Green box) */}
                        <div className="stat-card notification-override-card" style={{ border: notificationMode !== 'default' ? '3px solid #10b981' : '1px solid #e2e8f0' }}>
                            <div className="stat-icon">{notificationMode === 'email' ? '📧' : notificationMode === 'whatsapp' ? '💬' : '⚙️'}</div>
                            <div className="stat-content">
                                <h3>Notification Mode</h3>
                                <div className="stat-value" style={{ fontSize: '1.2rem', marginBottom: '8px', color: notificationMode !== 'default' ? '#10b981' : '#64748b' }}>
                                    {notificationMode === 'default' ? 'Default (Per User)' : `Forced: ${notificationMode.toUpperCase()}`}
                                </div>
                                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                    <button
                                        onClick={() => updateNotificationMode('default')}
                                        className={`mode-btn ${notificationMode === 'default' ? 'active' : ''}`}
                                        style={{ padding: '4px 8px', fontSize: '10px' }}
                                    >Default</button>
                                    <button
                                        onClick={() => updateNotificationMode('email')}
                                        className={`mode-btn ${notificationMode === 'email' ? 'active' : ''}`}
                                        style={{ padding: '4px 8px', fontSize: '10px' }}
                                    >Email</button>
                                    <button
                                        onClick={() => updateNotificationMode('whatsapp')}
                                        className={`mode-btn ${notificationMode === 'whatsapp' ? 'active' : ''}`}
                                        style={{ padding: '4px 8px', fontSize: '10px' }}
                                    >WA</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Report Generation Section */}
                <div className="report-section">
                    <h3>📋 Generate Reports</h3>
                    <p className="report-description">Download Power BI compatible CSV reports for analysis</p>

                    {/* Quick View Reports */}
                    <div className="report-row">
                        <h4>📊 Quick View</h4>
                        <div className="report-buttons">
                            <button
                                onClick={() => viewDetailedReport('daily')}
                                className="btn-report btn-view"
                                disabled={loading}
                            >
                                📅 Today's Report
                            </button>
                            <button
                                onClick={() => viewDetailedReport('weekly')}
                                className="btn-report btn-view"
                                disabled={loading}
                            >
                                📆 This Week
                            </button>
                            <button
                                onClick={() => viewDetailedReport('monthly')}
                                className="btn-report btn-view"
                                disabled={loading}
                            >
                                📈 Monthly ({selectedMonth})
                            </button>
                        </div>
                    </div>

                    {/* Custom Date Selector */}
                    <div className="report-row custom-date-row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <input
                                type="checkbox"
                                id="useCustomDates"
                                checked={useCustomDates}
                                onChange={(e) => setUseCustomDates(e.target.checked)}
                                style={{ marginRight: '8px', width: 'auto' }}
                            />
                            <label htmlFor="useCustomDates" style={{ fontWeight: 'bold' }}>Use Custom Date Range</label>
                        </div>

                        {useCustomDates && (
                            <div className="date-inputs" style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                <div className="date-field">
                                    <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Start Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                                <div className="date-field">
                                    <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>End Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* CSV Downloads */}
                    <div className="report-row">
                        <h4>⬇️ Download CSV (Power BI Ready)</h4>
                        <div className="report-buttons">
                            <button
                                onClick={() => downloadReport('monthly')}
                                className="btn-report btn-csv"
                                disabled={loading || useCustomDates}
                                title={useCustomDates ? "Monthly report must use the month selector" : "Download monthly summary"}
                                style={useCustomDates ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                                📊 Monthly Summary
                            </button>
                            <button
                                onClick={() => downloadReport('bookings')}
                                className="btn-report btn-csv"
                                disabled={loading || (useCustomDates && (!customStartDate || !customEndDate))}
                            >
                                📋 All Bookings
                            </button>
                            <button
                                onClick={() => downloadReport('attendance')}
                                className="btn-report btn-csv"
                                disabled={loading || (useCustomDates && (!customStartDate || !customEndDate))}
                            >
                                👥 Attendance
                            </button>
                            <button
                                onClick={() => downloadReport('unit-summary')}
                                className="btn-report btn-csv"
                                disabled={loading || (useCustomDates && (!customStartDate || !customEndDate))}
                            >
                                🏢 Unit Summary
                            </button>
                        </div>
                    </div>

                    {/* Legacy Reports */}
                    <div className="report-row">
                        <h4>📄 Executive Report</h4>
                        <div className="report-buttons">
                            <button
                                onClick={() => generateExecutiveSummaryPDF()}
                                className="btn-report btn-executive"
                                disabled={loading}
                            >
                                📊 Generate Executive Summary PDF
                            </button>
                        </div>
                        <p className="report-description" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            Comprehensive AI-generated PDF report with daily, weekly, and monthly analytics, charts, trends, and recommendations
                        </p>
                    </div>

                    {/* SharePoint Integration */}
                    <div className="report-row sharepoint-section">
                        <h4>☁️ SharePoint Integration</h4>
                        {sharePointStatus?.enabled ? (
                            <>
                                <p className="sharepoint-status sharepoint-connected">
                                    ✅ Connected to SharePoint
                                </p>
                                <div className="report-buttons">
                                    <button
                                        onClick={() => uploadToSharePoint('monthly')}
                                        className="btn-report btn-sharepoint"
                                        disabled={uploadingToSharePoint}
                                    >
                                        ☁️ Upload Monthly
                                    </button>
                                    <button
                                        onClick={() => uploadToSharePoint('bookings')}
                                        className="btn-report btn-sharepoint"
                                        disabled={uploadingToSharePoint}
                                    >
                                        ☁️ Upload Bookings
                                    </button>
                                    <button
                                        onClick={() => uploadToSharePoint('attendance')}
                                        className="btn-report btn-sharepoint"
                                        disabled={uploadingToSharePoint}
                                    >
                                        ☁️ Upload Attendance
                                    </button>
                                    <button
                                        onClick={() => uploadAllToSharePoint()}
                                        className="btn-report btn-sharepoint-all"
                                        disabled={uploadingToSharePoint}
                                    >
                                        ☁️ Upload All Reports
                                    </button>
                                </div>
                                {uploadingToSharePoint && (
                                    <p className="uploading-status">⏳ Uploading to SharePoint...</p>
                                )}
                            </>
                        ) : (
                            <div className="sharepoint-not-configured">
                                <p className="sharepoint-status sharepoint-disconnected">
                                    ⚠️ SharePoint not configured
                                </p>
                                <p className="sharepoint-help">
                                    To enable SharePoint integration, set the following environment variables:
                                </p>
                                <ul className="sharepoint-env-list">
                                    <li><code>AZURE_TENANT_ID</code> - Your Azure AD tenant ID</li>
                                    <li><code>AZURE_CLIENT_ID</code> - App registration client ID</li>
                                    <li><code>AZURE_CLIENT_SECRET</code> - App registration client secret</li>
                                    <li><code>SHAREPOINT_SITE_ID</code> - (Optional) Target site ID</li>
                                    <li><code>SHAREPOINT_DRIVE_ID</code> - (Optional) Document library ID</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Report Scheduler (Phase 4) */}
                    <div className="report-row scheduler-section">
                        <h4>⏰ Automated Report Scheduler</h4>
                        {schedulerStatus?.enabled ? (
                            <>
                                <p className="scheduler-status scheduler-active">
                                    ✅ Scheduler Active
                                </p>
                                <div className="scheduler-info">
                                    <p><strong>Schedule:</strong></p>
                                    <ul>
                                        <li>Daily reports at {schedulerStatus.dailyReportTime}</li>
                                        <li>Weekly reports on {schedulerStatus.weeklyReportDay}</li>
                                        <li>Monthly reports on day {schedulerStatus.monthlyReportDay}</li>
                                    </ul>
                                    {schedulerStatus.recipients?.length > 0 && (
                                        <p><strong>Recipients:</strong> {schedulerStatus.recipients.join(', ')}</p>
                                    )}
                                    {schedulerStatus.emailConfigured && (
                                        <p className="email-configured">📧 Email delivery configured</p>
                                    )}
                                </div>
                                <p style={{ marginTop: '10px', marginBottom: '8px', fontWeight: '600' }}>Manual Trigger:</p>
                                <div className="report-buttons">
                                    <button
                                        onClick={() => openEmailPopup('daily')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        📅 Generate Daily Report
                                    </button>
                                    <button
                                        onClick={() => openEmailPopup('weekly')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        📆 Generate Weekly Report
                                    </button>
                                    <button
                                        onClick={() => openEmailPopup('monthly')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        🗓️ Generate Monthly Report
                                    </button>
                                </div>
                                {triggeringReport && (
                                    <p className="triggering-status">⏳ Generating report...</p>
                                )}
                            </>
                        ) : (
                            <div className="scheduler-not-configured">
                                <p className="scheduler-status scheduler-inactive">
                                    ⚠️ Scheduler not enabled
                                </p>
                                <p className="scheduler-help">
                                    To enable automated report scheduling, set the following environment variables:
                                </p>
                                <ul className="scheduler-env-list">
                                    <li><code>REPORT_SCHEDULER_ENABLED=true</code></li>
                                    <li><code>DAILY_REPORT_TIME=08:00</code> (default)</li>
                                    <li><code>WEEKLY_REPORT_DAY=1</code> (1=Monday)</li>
                                    <li><code>MONTHLY_REPORT_DAY=1</code> (1st of month)</li>
                                    <li><code>REPORT_RECIPIENTS=admin@example.com,user@example.com</code></li>
                                </ul>
                                <p style={{ marginTop: '10px', marginBottom: '8px', fontWeight: '600' }}>Manual Report Generation:</p>
                                <div className="report-buttons">
                                    <button
                                        onClick={() => openEmailPopup('daily')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        📅 Generate Daily Report
                                    </button>
                                    <button
                                        onClick={() => openEmailPopup('weekly')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        📆 Generate Weekly Report
                                    </button>
                                    <button
                                        onClick={() => openEmailPopup('monthly')}
                                        className="btn-report btn-scheduler"
                                        disabled={triggeringReport}
                                    >
                                        🗓️ Generate Monthly Report
                                    </button>
                                </div>
                                {triggeringReport && (
                                    <p className="triggering-status">⏳ Generating report...</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Top Desks */}
                {analytics?.topDesks && analytics.topDesks.length > 0 && (
                    <div className="top-desks-section">
                        <h2>Top 5 Most Reserved Desks</h2>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Desk ID</th>
                                    <th>Zone</th>
                                    <th>Unit</th>
                                    <th>Bookings</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.topDesks.map((desk, index) => (
                                    <tr key={desk.desk_id}>
                                        <td>{index + 1}</td>
                                        <td><strong>{desk.desk_id}</strong></td>
                                        <td>{desk.zone}</td>
                                        <td>{desk.unit}</td>
                                        <td>{desk.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Top Users */}
                {analytics?.topUsers && analytics.topUsers.length > 0 && (
                    <div className="top-users-section">
                        <h2>Top 5 Most Active Users</h2>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Unit</th>
                                    <th>Bookings</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.topUsers.map((userStat, index) => (
                                    <tr key={userStat.user_email}>
                                        <td>{index + 1}</td>
                                        <td>{userStat.user_name}</td>
                                        <td>{userStat.user_email}</td>
                                        <td>{userStat.unit}</td>
                                        <td>{userStat.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* All Bookings Management */}
                <div className="bookings-management">
                    <h2>Manage All Bookings</h2>

                    <div className="filters">
                        <input
                            type="text"
                            placeholder="Search by user, email, or desk..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <div className="date-range-filter">
                            <label>From:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="date-input"
                            />
                            <label>To:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="date-input"
                            />
                            {(startDate || endDate) && (
                                <button
                                    type="button"
                                    className="btn-clear-dates"
                                    onClick={() => { setStartDate(''); setEndDate(''); }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Status</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <select
                            value={filterArrival}
                            onChange={(e) => setFilterArrival(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Arrivals</option>
                            <option value="confirmed">✅ Confirmed</option>
                            <option value="pending">⏳ Pending</option>
                        </select>
                    </div>

                    <div className="bookings-count">
                        Showing {filteredBookings.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredBookings.length)} of {filteredBookings.length} bookings
                        {filteredBookings.length !== bookings.length && ` (filtered from ${bookings.length} total)`}
                    </div>

                    <div className="bookings-table-wrapper">
                        <table className="data-table bookings-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Desk</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Arrival</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                                            No bookings found
                                        </td>
                                    </tr>
                                ) : (
                                    currentBookings.map(booking => (
                                        <tr key={booking.id} className={booking.status === 'cancelled' ? 'cancelled-row' : ''}>
                                            <td>{booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : 'N/A'}</td>
                                            <td><strong>{booking.desk_id}</strong></td>
                                            <td>{booking.user_name || 'N/A'}</td>
                                            <td>{booking.user_email}</td>
                                            <td>
                                                {booking.start_time && booking.end_time
                                                    ? `${booking.start_time} - ${booking.end_time}`
                                                    : 'Full Day'}
                                            </td>
                                            <td>
                                                <span className={`status-badge status-${booking.status}`}>
                                                    {booking.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`arrival-badge ${booking.checkin_confirmed_at ? 'arrival-confirmed' : 'arrival-pending'}`}>
                                                    {booking.checkin_confirmed_at
                                                        ? '✅ Confirmed'
                                                        : booking.status === 'cancelled'
                                                            ? '—'
                                                            : '⏳ Pending'}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => {
                                                        if (!booking.id) {
                                                            alert('Invalid booking ID');
                                                            return;
                                                        }
                                                        setEditingBooking(booking);
                                                        setEditForm({
                                                            user_email: booking.user_email,
                                                            desk_id: booking.desk_id,
                                                            booking_date: booking.booking_date,
                                                            start_time: booking.start_time || '',
                                                            end_time: booking.end_time || '',
                                                            status: booking.status,
                                                            checkin_confirmed_at: booking.checkin_confirmed_at || null
                                                        });
                                                    }}
                                                    className="btn-edit-small"
                                                >
                                                    Edit
                                                </button>
                                                {['confirmed', 'checked_in'].includes(booking.status) && booking.id && (
                                                    <button
                                                        onClick={() => handleCancelBooking(booking.id)}
                                                        className="btn-cancel-small"
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {filteredBookings.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="btn-pagination"
                            >
                                ← Previous
                            </button>
                            <span className="pagination-info">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="btn-pagination"
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>

                {/* All Desk Status Section */}
                <div className="analytics-section">
                    <h3>📍 All Desk Status</h3>
                    <div className="bookings-count" style={{ marginBottom: '1rem' }}>
                        Showing {allDesks.length > 0 ? indexOfFirstDesk + 1 : 0}-{Math.min(indexOfLastDesk, allDesks.length)} of {allDesks.length} desks
                    </div>
                    <div className="desk-status-container">
                        <table className="desk-status-table">
                            <thead>
                                <tr>
                                    <th>Desk ID</th>
                                    <th>Zone</th>
                                    <th>Unit</th>
                                    <th>Monitor Setup</th>
                                    <th>Admin Status</th>
                                    <th>Booking Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentDesks.map(desk => (
                                    <tr key={desk.id} className={desk.status !== 'available' ? 'desk-unavailable' : ''}>
                                        <td><strong>{desk.id}</strong></td>
                                        <td>{desk.zone || 'N/A'}</td>
                                        <td>{desk.unit || 'N/A'}</td>
                                        <td>
                                            <span className={`monitor-badge monitor-${(desk.monitor_setup || '').toLowerCase().includes('dual') ? 'dual' :
                                                (desk.monitor_setup || '').toLowerCase().includes('single') ? 'single' :
                                                    'none'
                                                }`}>
                                                {desk.monitor_setup || 'No Monitor'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${desk.status}`}>
                                                {desk.status}
                                            </span>
                                        </td>
                                        <td>
                                            {desk.hasActiveBookings ? (
                                                <span className="booking-status booking-occupied">
                                                    📅 {desk.activeBookingCount} booking{desk.activeBookingCount > 1 ? 's' : ''}
                                                </span>
                                            ) : (
                                                <span className="booking-status booking-free">
                                                    ✅ Available
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {allDesks.length === 0 && (
                            <div className="no-data">
                                <p>No desks found</p>
                            </div>
                        )}
                    </div>

                    {/* Desk Pagination Controls */}
                    {allDesks.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <button
                                onClick={() => setCurrentDeskPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentDeskPage === 1}
                                className="btn-pagination"
                            >
                                ← Previous
                            </button>
                            <span className="pagination-info">
                                Page {currentDeskPage} of {totalDeskPages}
                            </span>
                            <button
                                onClick={() => setCurrentDeskPage(prev => Math.min(prev + 1, totalDeskPages))}
                                disabled={currentDeskPage === totalDeskPages}
                                className="btn-pagination"
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Booking Modal */}
            {editingBooking && (
                <div className="modal-overlay" onClick={() => setEditingBooking(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Edit Booking</h3>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleEditBooking(editForm);
                        }}>
                            <div className="form-group">
                                <label>User Email:</label>
                                <input name="user_email" value={editForm.user_email} onChange={(e) => setEditForm({ ...editForm, user_email: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Desk ID:</label>
                                <input name="desk_id" type="text" value={editForm.desk_id || ''} onChange={(e) => setEditForm({ ...editForm, desk_id: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Booking Date:</label>
                                <input name="booking_date" type="date" value={editForm.booking_date} onChange={(e) => setEditForm({ ...editForm, booking_date: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Start Time:</label>
                                <input name="start_time" type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>End Time:</label>
                                <input name="end_time" type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Status:</label>
                                <select name="status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '15px', marginTop: '15px' }}>
                                <label><strong>Arrival Status:</strong></label>
                                {editForm.checkin_confirmed_at ? (
                                    <div style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #4caf50' }}>
                                        <div style={{ color: '#2e7d32', fontWeight: '600', marginBottom: '5px' }}>
                                            ✅ Arrival Confirmed
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#666' }}>
                                            Confirmed at: {new Date(editForm.checkin_confirmed_at).toLocaleString()}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setEditForm({ ...editForm, checkin_confirmed_at: null })}
                                            style={{
                                                marginTop: '8px',
                                                padding: '6px 12px',
                                                backgroundColor: '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Clear Confirmation
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ padding: '10px', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ff9800' }}>
                                        <div style={{ color: '#e65100', fontWeight: '600', marginBottom: '5px' }}>
                                            ⏳ Not Confirmed
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                                            User has not confirmed arrival yet
                                        </div>
                                        {editForm.status === 'confirmed' && (
                                            <button
                                                type="button"
                                                onClick={() => setEditForm({ ...editForm, checkin_confirmed_at: new Date().toISOString() })}
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: '#4caf50',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                ✓ Confirm Arrival Manually
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-actions">
                                <button type="submit" className="btn-primary">Update</button>
                                <button type="button" onClick={() => setEditingBooking(null)} className="btn-secondary">Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Email Popup Modal for Manual Report Generation */}
            {emailPopup.show && (
                <div className="report-modal-overlay" onClick={closeEmailPopup}>
                    <div className="email-popup-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="report-modal-header">
                            <div className="report-modal-title">
                                <h2>📧 Send {emailPopup.reportType.charAt(0).toUpperCase() + emailPopup.reportType.slice(1)} Report</h2>
                                <span className="report-modal-subtitle">Enter email addresses to receive the report</span>
                            </div>
                            <button className="report-modal-close" onClick={closeEmailPopup}>×</button>
                        </div>
                        <div className="email-popup-content">
                            <div className="email-input-group">
                                <label htmlFor="reportEmails">
                                    <strong>Recipient Email(s):</strong>
                                    <span className="email-help-text">Separate multiple emails with commas</span>
                                </label>
                                <textarea
                                    id="reportEmails"
                                    className="email-input-textarea"
                                    value={emailPopup.emails}
                                    onChange={(e) => setEmailPopup({ ...emailPopup, emails: e.target.value })}
                                    placeholder="email1@example.com, email2@example.com"
                                    rows={3}
                                />
                            </div>
                            <div className="email-preview">
                                <strong>Recipients Preview:</strong>
                                <div className="email-tags">
                                    {emailPopup.emails.split(',').filter(e => e.trim()).map((email, idx) => (
                                        <span key={idx} className="email-tag">{email.trim()}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="report-modal-footer">
                            <button className="btn-secondary" onClick={closeEmailPopup}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={() => triggerScheduledReport(emailPopup.reportType, emailPopup.emails)}
                                disabled={!emailPopup.emails.trim() || triggeringReport}
                            >
                                {triggeringReport ? '⏳ Generating...' : '📤 Generate & Send Report'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Professional Report Modal */}
            {reportModal && (
                <div className="report-modal-overlay" onClick={closeReportModal}>
                    <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="report-modal-header">
                            <div className="report-modal-title">
                                <h2>{reportModal.title}</h2>
                                <span className="report-modal-subtitle">{reportModal.subtitle}</span>
                            </div>
                            <button className="report-modal-close" onClick={closeReportModal}>×</button>
                        </div>
                        <div className="report-modal-content">
                            {reportModal.sections.map((section, idx) => (
                                <div key={idx} className="report-section-card">
                                    <h3 className="report-section-title">
                                        <span className="section-icon">{section.icon}</span>
                                        {section.title}
                                    </h3>
                                    <div className="report-section-items">
                                        {section.items.map((item, itemIdx) => (
                                            <div key={itemIdx} className="report-item">
                                                <span className="report-item-label">{item.label}</span>
                                                <span className="report-item-value">{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="report-modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    navigator.clipboard.writeText(reportModal.rawText);
                                    alert('Report copied to clipboard!');
                                }}
                            >
                                📋 Copy to Clipboard
                            </button>
                            <button
                                className="btn-secondary"
                                onClick={() => window.print()}
                            >
                                🖨️ Print
                            </button>
                            <button className="btn-primary" onClick={closeReportModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard;
